import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Modal,
  ProgressBar,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  type SelectProps,
  type TableProps,
} from '@cloudscape-design/components';
import {
  fetchDashboard,
  fetchTemplates,
  createWorkflow,
  type DashboardWorkflow,
  type DashboardSummary,
  type WorkflowTemplate,
} from '../api/onboarding-manager';
import { fetchTenants } from '../api/tenants';
import type { Tenant } from '../types/tenant';

const STATUS_INDICATOR: Record<string, { type: 'pending' | 'in-progress' | 'success' | 'error' | 'warning' | 'stopped'; label: string }> = {
  not_started: { type: 'pending', label: 'Not Started' },
  in_progress: { type: 'in-progress', label: 'In Progress' },
  blocked: { type: 'error', label: 'Blocked' },
  stalled: { type: 'warning', label: 'Stalled' },
  completed: { type: 'success', label: 'Completed' },
  cancelled: { type: 'stopped', label: 'Cancelled' },
};

function formatDuration(minutes: number | null): string {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function OnboardingDashboard() {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<DashboardWorkflow[]>([]);
  const [summary, setSummary] = useState<DashboardSummary>({ total: 0, inProgress: 0, stalled: 0, completed: 0, blocked: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Start workflow modal
  const [showStartModal, setShowStartModal] = useState(false);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<SelectProps.Option | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<SelectProps.Option | null>(null);
  const [assignedTo, setAssignedTo] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadDashboard(); }, []);

  async function loadDashboard() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDashboard();
      setWorkflows(data.workflows);
      setSummary(data.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }

  async function openStartModal() {
    setShowStartModal(true);
    try {
      const [tmplRes, tenantRes] = await Promise.all([fetchTemplates(), fetchTenants()]);
      setTemplates(tmplRes.templates);
      setTenants(tenantRes.tenants);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    }
  }

  async function handleStartWorkflow() {
    if (!selectedTenant?.value || !selectedTemplate?.value) return;
    setSubmitting(true);
    try {
      await createWorkflow({
        tenantId: selectedTenant.value,
        templateId: selectedTemplate.value,
        assignedTo: assignedTo || undefined,
        customerContact: contactName ? { name: contactName, email: contactEmail } : undefined,
      });
      setShowStartModal(false);
      setSelectedTenant(null);
      setSelectedTemplate(null);
      setAssignedTo('');
      setContactName('');
      setContactEmail('');
      loadDashboard();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start workflow');
    } finally {
      setSubmitting(false);
    }
  }

  const columns: TableProps.ColumnDefinition<DashboardWorkflow>[] = [
    {
      id: 'tenant',
      header: 'Tenant',
      width: 160,
      cell: (item) => (
        <Button variant="inline-link" onClick={() => navigate(`/ops/onboarding/${item.workflowId}?tenantId=${item.tenantId}`)}>
          {item.customerContact?.name || item.tenantId.slice(0, 8)}
        </Button>
      ),
    },
    {
      id: 'template',
      header: 'Workflow',
      width: 180,
      cell: (item) => item.templateName,
    },
    {
      id: 'status',
      header: 'Status',
      width: 130,
      cell: (item) => {
        const s = STATUS_INDICATOR[item.isStalled ? 'stalled' : item.status] || STATUS_INDICATOR.in_progress;
        return <StatusIndicator type={s.type}>{s.label}</StatusIndicator>;
      },
    },
    {
      id: 'progress',
      header: 'Progress',
      width: 200,
      cell: (item) => (
        <ProgressBar
          value={item.percentComplete}
          label={`${item.completedSteps}/${item.totalSteps} steps`}
          variant="standalone"
          status={item.status === 'completed' ? 'success' : item.isStalled ? 'error' : 'in-progress'}
        />
      ),
    },
    {
      id: 'currentStep',
      header: 'Current Step',
      width: 200,
      cell: (item) => (
        <SpaceBetween direction="horizontal" size="xxs">
          <Badge color="blue">{item.currentStep + 1}</Badge>
          <span>{item.currentStepTitle}</span>
        </SpaceBetween>
      ),
    },
    {
      id: 'stall',
      header: 'Stall',
      width: 80,
      cell: (item) => item.isStalled
        ? <Badge color="red">{formatDuration(item.stallDuration)}</Badge>
        : <span style={{ color: '#687078' }}>—</span>,
    },
    {
      id: 'assignedTo',
      header: 'Assigned',
      width: 120,
      cell: (item) => item.assignedTo || '—',
    },
    {
      id: 'lastActivity',
      header: 'Last Activity',
      width: 150,
      cell: (item) => formatDate(item.lastActivity),
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Track customer onboarding workflows across all products and engagements."
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" onClick={loadDashboard}>Refresh</Button>
            <Button variant="primary" onClick={openStartModal}>Start Onboarding</Button>
          </SpaceBetween>
        }
      >
        Onboarding Manager
      </Header>

      {/* Summary Cards */}
      <ColumnLayout columns={4} variant="text-grid">
        <Container>
          <Box variant="awsui-key-label">Total</Box>
          <Box variant="awsui-value-large">{summary.total}</Box>
        </Container>
        <Container>
          <Box variant="awsui-key-label">In Progress</Box>
          <Box variant="awsui-value-large">{summary.inProgress}</Box>
        </Container>
        <Container>
          <Box variant="awsui-key-label">
            <SpaceBetween direction="horizontal" size="xxs">
              <span>Stalled</span>
              {summary.stalled > 0 && <Badge color="red">{summary.stalled}</Badge>}
            </SpaceBetween>
          </Box>
          <Box variant="awsui-value-large" color={summary.stalled > 0 ? 'text-status-error' : undefined}>
            {summary.stalled}
          </Box>
        </Container>
        <Container>
          <Box variant="awsui-key-label">Completed</Box>
          <Box variant="awsui-value-large" color="text-status-success">{summary.completed}</Box>
        </Container>
      </ColumnLayout>

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}

      {/* Workflows Table */}
      <Table
        columnDefinitions={columns}
        items={workflows}
        loading={loading}
        loadingText="Loading onboarding workflows..."
        trackBy="workflowId"
        variant="full-page"
        stickyHeader
        stripedRows
        sortingDisabled
        onRowClick={({ detail }) => navigate(`/ops/onboarding/${detail.item.workflowId}?tenantId=${detail.item.tenantId}`)}
        empty={
          <Box textAlign="center" padding="l">
            <Box variant="p" color="text-status-inactive">No onboarding workflows yet.</Box>
            <Button variant="primary" onClick={openStartModal}>Start Onboarding</Button>
          </Box>
        }
        header={<Header counter={`(${workflows.length})`}>Active Workflows</Header>}
      />

      {/* Start Workflow Modal */}
      <Modal
        visible={showStartModal}
        onDismiss={() => setShowStartModal(false)}
        header="Start Onboarding Workflow"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowStartModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleStartWorkflow} loading={submitting} disabled={!selectedTenant || !selectedTemplate}>
                Start
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Tenant">
            <Select
              selectedOption={selectedTenant}
              onChange={({ detail }) => setSelectedTenant(detail.selectedOption)}
              options={tenants.map((t) => ({
                label: `${t.name} — ${t.companyName}`,
                value: t.tenantId,
                description: t.contactEmail,
              }))}
              placeholder="Select a tenant..."
              filteringType="auto"
            />
          </FormField>
          <FormField label="Workflow Template">
            <Select
              selectedOption={selectedTemplate}
              onChange={({ detail }) => setSelectedTemplate(detail.selectedOption)}
              options={templates.map((t) => ({
                label: t.name,
                value: t.templateId,
                description: `${t.steps.length} steps · ${t.productId}`,
              }))}
              placeholder="Select a template..."
            />
          </FormField>
          <FormField label="Assigned Operator" description="Who is managing this onboarding?">
            <Input value={assignedTo} onChange={({ detail }) => setAssignedTo(detail.value)} placeholder="e.g. will@gdna.io" />
          </FormField>
          <FormField label="Customer Contact Name">
            <Input value={contactName} onChange={({ detail }) => setContactName(detail.value)} placeholder="e.g. Vivek" />
          </FormField>
          <FormField label="Customer Contact Email">
            <Input value={contactEmail} onChange={({ detail }) => setContactEmail(detail.value)} placeholder="e.g. vivek@customer.com" type="email" />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
